import React from 'react';
import { Heart, Maximize2, Home, Key, Layers, Award, Camera, Warehouse, Building2, Trees } from 'lucide-react';
import { Apartment } from '../types';

interface PropertyCardProps {
  key?: string;
  apartment: Apartment;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
  onSelect: (apartment: Apartment) => void;
  onDelete?: (id: string) => void; // for Admin management if needed
  isAdminView?: boolean;
}

export default function PropertyCard({ 
  apartment, 
  isFavorite, 
  onToggleFavorite, 
  onSelect, 
  onDelete,
  isAdminView = false
}: PropertyCardProps) {
  const estateTypeLower = String(apartment.estateType || '').toLowerCase();
  const titleLower = (typeof apartment.title === 'object' ? (apartment.title as any)?.name || '' : String(apartment.title || '')).toLowerCase();
  const isPlot = estateTypeLower.includes('парцел') || 
                 estateTypeLower.includes('упи') || 
                 estateTypeLower.includes('земедел') || 
                 estateTypeLower.includes('поземлен') || 
                 estateTypeLower.includes('терен') || 
                 estateTypeLower.includes('нива') || 
                 estateTypeLower.includes('инвест');

  const isGarage = !isPlot && (
    estateTypeLower.includes('гараж') || 
    estateTypeLower.includes('паркомяст')
  );

  const isHouse = !isPlot && !isGarage && (
    estateTypeLower.includes('къщ') || 
    estateTypeLower.includes('вила')
  );

  const isCommercial = !isPlot && !isGarage && !isHouse && (
    estateTypeLower.includes('офис') || 
    estateTypeLower.includes('магазин') || 
    estateTypeLower.includes('заведение') ||
    estateTypeLower.includes('склад') ||
    estateTypeLower.includes('пром')
  );

  // Formatting helper
  const formatNum = (num: number) => {
    return num.toLocaleString('bg-BG');
  };

  const zoom = (apartment.imageZoom !== undefined ? apartment.imageZoom : 100) / 100;
  const x = apartment.imagePanX !== undefined ? apartment.imagePanX : 50;
  const y = apartment.imagePanY !== undefined ? apartment.imagePanY : 50;
  const imageStyle = {
    transform: `scale(${zoom})`,
    objectPosition: `${x}% ${y}%`,
    transformOrigin: `${x}% ${y}%`,
  };

  return (
    <div 
      role="button"
      tabIndex={0}
      aria-label={`Отвори оферта: ${typeof apartment.title === 'object' ? (apartment.title as any)?.name : apartment.title}`}
      onClick={() => onSelect(apartment)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(apartment);
        }
      }}
      className="bg-white rounded-xl shadow-md hover:shadow-2xl hover:border-slate-300 transition-all duration-300 overflow-hidden flex flex-col group border border-gray-100 h-full relative cursor-pointer active:scale-[0.99] select-none"
    >
      {/* Photo header */}
      <div 
        onClick={() => onSelect(apartment)}
        className="relative h-56 overflow-hidden bg-slate-100 shrink-0 flex items-center justify-center cursor-pointer"
      >
        <img 
          src={apartment.image} 
          alt="" 
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover blur-sm scale-110 opacity-30 pointer-events-none"
        />
        <img 
          src={apartment.image} 
          alt={apartment.title}
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover transition-transform duration-500 relative z-10"
          style={imageStyle}
          onError={(e) => {
            // fallback
            (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=600&auto=format&fit=crop&q=80';
          }}
        />

        {/* Favorite Heart Trigger */}
        <button 
          id={`favorite-btn-${apartment.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(apartment.id);
          }}
          className="absolute top-3 right-3 p-2 rounded-full bg-white/95 text-gray-700 hover:text-red-500 hover:scale-110 active:scale-95 transition-all shadow-md z-10"
        >
          <Heart className={`w-4 h-4 transition-colors ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} />
        </button>

        {/* Tags / Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 max-w-[80%]">
          {/* Main Sale/Rent tag */}
          <span className={`px-2.5 py-1 text-[10px] font-bold tracking-wider rounded text-white shadow-sm font-sans uppercase ${
            apartment.type === 'sale' ? 'bg-[#C5A46E]' : 'bg-[#1D608A]'
          }`}>
            {apartment.type === 'sale' ? 'Продава' : 'НАЕМ'}
          </span>

          {/* Additional status tags */}
          {Array.isArray(apartment.tags) && apartment.tags.map((tag, idx) => {
            const val = typeof tag === 'object' ? (tag as any)?.name || (tag as any)?.title || '' : String(tag || '');
            if (!val) return null;
            const upper = val.toUpperCase();
            // Do not show internal admin tags or Act 16 tags
            if (upper.includes('ХЕРО') || upper.includes('ГЛАВНА ОФЕРТА')) return null;
            if (upper.includes('АКТ 16') || upper.includes('АКТ16') || upper.includes('ACT 16') || upper.includes('АКТ')) return null;
            return (
              <span 
                key={idx} 
                className="px-2.5 py-1 text-[10px] font-extrabold tracking-wider bg-[#0F1E32]/90 text-[#C5A46E] rounded shadow-sm font-sans uppercase"
              >
                {val}
              </span>
            );
          })}
        </div>

        {/* Photo count indicator */}
        {Array.isArray(apartment.gallery) && apartment.gallery.length > 1 && (
          <div className="absolute bottom-3 right-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-950/75 backdrop-blur-sm text-white text-[11px] font-bold shadow-md border border-white/10 pointer-events-none">
            <Camera className="w-3.5 h-3.5 text-[#C5A46E]" />
            <span>{apartment.gallery.length}</span>
          </div>
        )}

        {/* Admin actions overlay */}
        {isAdminView && onDelete && (
          <div className="absolute bottom-3 left-3 z-10 flex gap-2">
            <button
              id={`admin-delete-btn-${apartment.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(apartment.id);
              }}
              className="px-3 py-1 bg-red-600 text-white rounded text-xs font-bold shadow hover:bg-red-700 transition"
            >
              Изтрий 🗑️
            </button>
          </div>
        )}
      </div>

      {/* Property Details Body */}
      <div 
        onClick={() => onSelect(apartment)}
        className="p-5 flex flex-col flex-grow cursor-pointer"
      >
        {/* Neighborhood status */}
        <div className="text-xs text-[#C5A46E] font-semibold tracking-wider uppercase mb-1 truncate">
          {(() => {
            const rawN = typeof apartment.neighborhood === 'object' 
              ? (apartment.neighborhood as any)?.name || '' 
              : String(apartment.neighborhood || '');
            const n = rawN.trim();
            if (!n) return 'Варна';
            if (n.toLowerCase().startsWith('с.')) return `${n}, обл. Варна`;
            if (n.toLowerCase().startsWith('гр.') || n.toLowerCase().includes('варна')) return n;
            return `${n}, Варна`;
          })()}
        </div>

        {/* Title */}
        <h3 
          onClick={(e) => {
            e.stopPropagation();
            onSelect(apartment);
          }}
          className="text-[#0F1E32] font-semibold text-base tracking-tight mb-2 group-hover:text-[#C5A46E] transition-colors line-clamp-1 cursor-pointer"
        >
          {typeof apartment.title === 'object' ? (apartment.title as any)?.name || 'Имот' : apartment.title}
        </h3>

        {/* Double Price Panel matching BG style */}
        <div 
          onClick={(e) => {
            e.stopPropagation();
            onSelect(apartment);
          }}
          className="mb-4 bg-slate-50 border-l-4 border-[#C5A46E] p-2.5 rounded-r cursor-pointer"
        >
          <div className="text-xl font-bold text-[#0F1E32]">
            {apartment.type === 'sale' ? (
              <>€ {formatNum(apartment.price)}</>
            ) : (
              <>{formatNum(apartment.price)} € <span className="text-xs font-normal text-gray-500">/ месец</span></>
            )}
          </div>
        </div>

        {/* Quick Specs Icons grid */}
        <div 
          onClick={(e) => {
            e.stopPropagation();
            onSelect(apartment);
          }}
          className="grid grid-cols-3 gap-2.5 text-xs text-gray-600 border-t border-gray-100 pt-3.5 mt-auto mb-4 cursor-pointer"
        >
          <div className="flex items-center gap-1.5">
            <Maximize2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span className="font-medium">{apartment.area} m²</span>
          </div>
          <div className="flex items-center gap-1.5">
            {isPlot ? (
              <Trees className="w-3.5 h-3.5 text-[#C5A46E] shrink-0" />
            ) : isGarage ? (
              <Warehouse className="w-3.5 h-3.5 text-[#C5A46E] shrink-0" />
            ) : isHouse ? (
              <Home className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            ) : isCommercial ? (
              <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            ) : (
              <Home className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            )}
            <span className="font-medium shrink-0 truncate">
              {isPlot
                ? 'Парцел'
                : isGarage 
                ? 'Гараж' 
                : isHouse 
                ? 'Къща' 
                : isCommercial 
                ? (apartment.estateType || 'Търговски') 
                : apartment.rooms > 0 ? `${apartment.rooms}-стаен` : (apartment.estateType || 'Имот')}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span className="font-medium text-gray-500 truncate">
              {isPlot ? 'Терен' : isGarage ? 'Партер' : `ет. ${typeof apartment.floor === 'object' ? (apartment.floor as any)?.name || '1' : (apartment.floor || '1')}`}
            </span>
          </div>
        </div>

        {/* Buttons / Broker / ID */}
        <div 
          onClick={(e) => {
            e.stopPropagation();
            onSelect(apartment);
          }}
          className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100 cursor-pointer"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="font-mono text-[10px] text-gray-400 tracking-wider shrink-0">
              ID: {apartment.id}
            </span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button 
              id={`view-more-btn-${apartment.id}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(apartment);
              }}
              className="text-xs text-[#C5A46E] font-bold group-hover:translate-x-1 hover:text-[#0F1E32] transition-colors duration-300 flex items-center gap-0.5 py-1 cursor-pointer focus:outline-none"
            >
              Виж повече &rarr;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
